import React, { useCallback, useContext, useMemo } from 'react'
import { CandlestickData, SingleValueData } from 'lightweight-charts'
import { AppContext } from '../../../App'
import { Security } from '../../../proto/generated/security'
import { ExtraSmallButton } from '../../custom'
import AddAlertIcon from '../../../assets/icons/AddAlertIcon'
import { ShortcutsCategories, ShortcutType } from '../../KeyboardShortcuts/Shortcuts.types'
import useShortcuts from '../../../hooks/useShortcuts'
import { AlertDialogShortcutsKeyName } from '../../KeyboardShortcuts/ShortcutsKeymap'
import { UserAlert } from '../../../proto/generated/nexus'
import { useDispatch, useSelector } from 'react-redux'
import { setAlertModalData } from '../../../reducer/alerts.reducer'
import { getFocusedAlertableSourceDrawing, resolveDrawingAlertModalPayload } from '../../../util/alerts'
import AnalyticsManager from '../../../analytics/AnalyticsManager'
import { Tooltip } from '@mui/material'
import { ShortcutTooltipTitle, ShortcutKeys } from '../../KeyboardShortcuts/ShortcutTooltipTitle'
import { Group, setFeatureRestrictionDialog } from '../../../reducer/user.reducer'
import { useUser } from '../../../context/UserContext'
import { FeatureCategory, FeatureSubCategory } from '../../adminUserRestrictions/Restrictions.constants'
import { selectUserSubscriptionPlansData } from '../../../selectors/user'
import { FeatureCode } from '../../../util/RestrictionFeatureCode'
import { RootState } from '../../../store'

export interface AlertData {
    grps: AlertSeriesGrp[];
    alert?: UserAlert,
    securities: Security[]
}

export interface AlertSeriesGrp {
    id: string;
    name: string;
    code: string;
    description?: string;
    serieses: AlertSeries[];
    primary: boolean;
}

export interface AlertSeries {
    id: string;
    name: string;
    data: CandlestickData | SingleValueData;
}

interface AlertMenuItemProps {
    viewKey: string,
    disabled?: boolean,
    customStyle?: any
    customPriceValue?: string
    handleCloseMenu?: Function
}

function AlertMenuItem(props: AlertMenuItemProps) {
    const dispatch = useDispatch()
    const { disabled = false, viewKey } = props
    const appContext = useContext(AppContext);
    const { user: { hasAccess } } = useUser()
    const subscriptionPlans = useSelector(selectUserSubscriptionPlansData)
    const alerts = useSelector((state: RootState) => state.alertsStore.alerts)

    const handleButtonClick = useCallback(() => {
        if(!hasAccess(FeatureCategory.CHART, FeatureSubCategory.ALERTS)) {
            dispatch(setFeatureRestrictionDialog({
                open: true,
                feature: "Alerts",
                featureRestrictionCode:FeatureCode.CHART_ALERTS,
                group: Group.CHART
            }))
            return
        }
        if (disabled) return; // Check manually for shortcut trigger;
        const chartSnapshotMethod = appContext.getChartSnapshot[viewKey]
        const snapshot = chartSnapshotMethod ? chartSnapshotMethod() : null;

        if (!snapshot) return
        const chart = appContext.getChart(viewKey)
        const sourceDrawing = chart ? getFocusedAlertableSourceDrawing(chart) : undefined
        const { alert, edit, sourceDrawing: resolvedDrawing } = resolveDrawingAlertModalPayload(
            alerts,
            sourceDrawing,
            snapshot
        )
        dispatch(setAlertModalData({
            chartSnapshot: snapshot,
            alert,
            edit,
            sourceDrawing: resolvedDrawing,
        }))
    }, [alerts, appContext, disabled, viewKey, dispatch, hasAccess])

    const shortcuts: ShortcutType = useMemo(() => ({
        [AlertDialogShortcutsKeyName.openAlertDialog]: () => {
            AnalyticsManager.logEvent('Create Alert Dialog Opened', { source: 'keyboard' })
            handleButtonClick()
        }
    }), [handleButtonClick])

    useShortcuts(ShortcutsCategories.ALERT_DIALOG, shortcuts)

    return (
        <div>
            <Tooltip
                 title={
                    <ShortcutTooltipTitle label="Create Alert">
                        <ShortcutKeys keys={['⌥', 'A']} />
                    </ShortcutTooltipTitle>
                }
                arrow
                placement="bottom"
            >
                <span>
                <ExtraSmallButton
                    id={'add-alert-menu-button'}
                    aria-label="Create Alert"
                    size="small"
                    color="inherit"
                    onClick={handleButtonClick}
                    disabled={disabled}
                    sx={{ width: '100%' }}
                    data-event={'StockBar Alert Menu Opened'}
                >
                    <AddAlertIcon />
                </ExtraSmallButton>
                </span>
            </Tooltip>
        </div>
    )
}

export default React.memo(AlertMenuItem)